import { z } from 'zod';
import { supabase } from '@/utils/supabase';
import { RECAPTURE_CHANGED_EVENT } from '@/services/recaptureService';
import { hasVolumeBonusFoundation } from './volumeBonusFoundationService';

const money = z.number().finite();
const uuid = z.string().uuid();
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const volumeBonusAgreementSchema = z.object({
  recipient_id: uuid,
  recipient_name: z.string().min(1),
  organization_id: uuid,
  requested_amount: money.min(10000),
  amount: money.min(10000),
  loan_count: z.number().int().min(10),
  accepted_date: day,
  agreement_version: z.string().min(1),
  available_before: money,
  available_after: money,
  installment: money.positive(),
  terms: z.array(z.string().min(1)).min(1),
  review_token: z.string().min(1)
}).refine(agreement => {
  const cents = (amount: number) => Math.round(amount * 100);
  return [agreement.requested_amount, agreement.amount, agreement.installment].every(amount => cents(amount) / 100 === amount)
    && Math.floor(cents(agreement.requested_amount) / agreement.loan_count) * agreement.loan_count === cents(agreement.amount)
    && cents(agreement.installment) * agreement.loan_count === cents(agreement.amount);
}, 'The equal installments must reconcile to the committed amount.');

export const volumeBonusInstallmentSchema = z.object({
  id: uuid,
  installment_number: z.number().int().positive(),
  amount: money.positive(),
  loan_number: z.string().min(1),
  banker_recon_id: uuid,
  purchase_date: day,
  allocated_at: z.string().min(1),
  payout_id: uuid,
  cash_paid: money.nonnegative(),
  remaining: money,
  paid: z.boolean()
});

export const volumeBonusPlanSchema = z.object({
  id: uuid,
  recipient_id: uuid,
  amount: money.min(10000),
  loan_count: z.number().int().min(10),
  accepted_at: z.string().min(1),
  accepted_date: day,
  agreement: volumeBonusAgreementSchema,
  returned_amount: money.nonnegative(),
  departed_at: z.string().nullable(),
  email_status: z.enum(['pending', 'sending', 'accepted', 'review_required']),
  email_accepted_at: z.string().nullable(),
  installments: z.array(volumeBonusInstallmentSchema)
});

const accountSchema = z.object({
  recipient_id: uuid,
  enabled: z.boolean(),
  email_enabled: z.boolean().optional(),
  eligible: z.boolean(),
  profitability: z.object({
    complete: z.boolean(),
    missing_rows: z.number().int().nonnegative(),
    earned: money.nullable(),
    absorbed: money,
    bonuses: money.optional(),
    volume_bonus_committed: money,
    available: money.nullable()
  }),
  plans: z.array(volumeBonusPlanSchema)
});

const reconPreviewSchema = z.object({
  applies: z.boolean(),
  amount: money.nonnegative(),
  reason: z.string().nullable(),
  plan_id: uuid.nullable(),
  installment_number: z.number().int().positive().nullable(),
  loan_count: z.number().int().min(10).nullable(),
  allocated: z.boolean(),
  recon_id: uuid,
  recipient_id: uuid.nullable(),
  loan_number: z.string(),
  purchase_date: day.nullable(),
  review_token: z.string().min(1)
});

export type VolumeBonusAgreement = z.infer<typeof volumeBonusAgreementSchema>;
export type VolumeBonusInstallment = z.infer<typeof volumeBonusInstallmentSchema>;
export type VolumeBonusPlan = z.infer<typeof volumeBonusPlanSchema>;
export type VolumeBonusAccount = z.infer<typeof accountSchema>;
export type VolumeBonusReconPreview = z.infer<typeof reconPreviewSchema>;

export interface VolumeBonusActivity {
  plan_id: string;
  recipient_id: string;
  date: string;
  amount: number;
  kind: 'elected' | 'returned';
}

export interface VolumeBonusReportMetadata {
  volume_bonus_activity?: VolumeBonusActivity[];
}

const MISSING_BACKEND_TTL_MS = 10 * 60 * 1000;
let backendInstalled = false;
let backendMissingUntil = 0;

/** Shared by account and recon probes; installation cannot be forgotten mid-session. */
export function getVolumeBonusBackendStatus(): 'unknown' | 'missing' | 'installed' {
  return backendInstalled ? 'installed' : Date.now() < backendMissingUntil ? 'missing' : 'unknown';
}

/** A pre-migration backend cannot contain accepted volume bonus obligations. */
function migrationNotInstalled(error: { code?: string }): boolean {
  if (error.code !== 'PGRST202' || backendInstalled) {
    return false;
  }
  backendMissingUntil = Date.now() + MISSING_BACKEND_TTL_MS;
  return true;
}

async function canReadVolumeBonusBackend(): Promise<boolean> {
  if (backendInstalled) {
    return true;
  }
  if (getVolumeBonusBackendStatus() === 'missing') {
    return false;
  }
  const foundation = await hasVolumeBonusFoundation();
  if (foundation === 'absent') {
    backendMissingUntil = Date.now() + MISSING_BACKEND_TTL_MS;
  }
  return foundation === 'present';
}

export async function getLoVolumeBonusAccount(recipientId: string): Promise<VolumeBonusAccount | null> {
  const args = { p_recipient_id: uuid.parse(recipientId) };
  if (!await canReadVolumeBonusBackend()) {
    return null;
  }
  const { data, error } = await supabase.rpc('get_lo_volume_bonus_account', args);
  if (error) {
    if (migrationNotInstalled(error)) {
      return null;
    }
    throw error;
  }
  backendInstalled = true;
  const result = accountSchema.parse(data);
  if (result.recipient_id !== recipientId || result.plans.some(plan => plan.recipient_id !== recipientId)) {
    throw new Error('The returned volume bonus account does not match the selected loan officer.');
  }
  return result;
}

export async function previewLoVolumeBonus(amount: number, loanCount: number): Promise<VolumeBonusAgreement> {
  const { data, error } = await supabase.rpc('preview_lo_volume_bonus', {
    p_amount: money.min(10000).parse(amount),
    p_loan_count: z.number().int().min(10).parse(loanCount)
  });
  if (error) {
    throw error;
  }
  return volumeBonusAgreementSchema.parse(data);
}

export async function acceptLoVolumeBonus(requestId: string, agreement: VolumeBonusAgreement): Promise<{ id: string; agreement: VolumeBonusAgreement; email_status: string }> {
  const { data, error } = await supabase.rpc('accept_lo_volume_bonus', {
    p_id: uuid.parse(requestId),
    p_amount: agreement.requested_amount,
    p_loan_count: agreement.loan_count,
    p_review_token: agreement.review_token
  });
  if (error) {
    throw error;
  }
  const result = z.object({ id: uuid, agreement: volumeBonusAgreementSchema, email_status: z.string() }).parse(data);
  if (result.id !== requestId || result.agreement.recipient_id !== agreement.recipient_id
    || result.agreement.requested_amount !== agreement.requested_amount || result.agreement.installment !== agreement.installment
    || result.agreement.amount !== agreement.amount || result.agreement.loan_count !== agreement.loan_count
    || result.agreement.review_token !== agreement.review_token) {
    throw new Error('Enrollment response could not be reconciled. Refresh your account before trying again.');
  }
  window.dispatchEvent(new Event(RECAPTURE_CHANGED_EVENT));
  return result;
}

export async function getBankerReconVolumeBonusPreview(reconId: string, recipientId: string | null, purchaseDate: string | null): Promise<VolumeBonusReconPreview | null> {
  const args = {
    p_banker_recon_id: uuid.parse(reconId),
    p_recipient_id: recipientId ? uuid.parse(recipientId) : null,
    p_purchase_date: purchaseDate ? day.parse(purchaseDate) : null
  };
  if (!await canReadVolumeBonusBackend()) {
    return null;
  }
  const { data, error } = await supabase.rpc('get_banker_recon_volume_bonus_preview', args);
  if (error) {
    if (migrationNotInstalled(error)) {
      return null;
    }
    throw error;
  }
  backendInstalled = true;
  const result = reconPreviewSchema.parse(data);
  if (result.applies && (!result.plan_id || !result.installment_number || !result.loan_count
    || result.installment_number > result.loan_count || result.amount <= 0)) {
    throw new Error('Volume bonus installment details could not be verified. Refresh before saving.');
  }
  if (result.recon_id !== reconId || result.recipient_id !== recipientId || result.purchase_date !== purchaseDate) {
    throw new Error('Volume bonus preview changed. Review the selected loan and purchase date.');
  }
  return result;
}
